import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import AnimatedSpotNeighbours from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-neighbours-light.json';
import { Box, Flex, Heading } from '@utilitywarehouse/hearth-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { DialogUnwrapped } from '@components/Dialog';
import { Layout } from '@components/Layout';
import { Loading } from '@components/Loading';
import { paths } from '@constants/paths';
import { APPOINTMENT_USER_ID } from '@constants/storageKeys';
import { Invite } from '@features/Remote/Invite';
import { useLoader } from '@hooks/useLoader';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { RequestState } from '@src/types/index';
import { useAppointmentId } from '@store/appointment/selectors';
import { getCurrentParticipantsSuffix } from '@utils/appointment';

interface RemoteInviteUIProps {
  title: string;
  isController: boolean;
  canStart: boolean;
  personLabel: string;
  isRemoteSupport: boolean;
  appointmentId: string;
  controllerName: string;
  errorMessage: string;
}

const RemoteInviteUI = ({
  title,
  isController,
  canStart,
  personLabel,
  isRemoteSupport,
  appointmentId,
  controllerName,
  errorMessage,
}: RemoteInviteUIProps) => (
  <Layout title={title}>
    {isController ? (
      <Invite
        code={appointmentId}
        canStart={canStart}
        personLabel={personLabel}
        isRemoteSupport={isRemoteSupport}
        errorMessage={errorMessage}
      />
    ) : (
      <DialogUnwrapped heading="" open preventDismiss hideCloseButton>
        <Flex direction="column" alignItems="center" spacing="xl">
          <Heading as="h2" size="lg" textAlign="center">
            Waiting for {controllerName} to start the appointment
          </Heading>
          <Box width="200px">
            <DotLottieReact
              data={AnimatedSpotNeighbours}
              loop
              autoplay
              className="invite__animation"
            />
          </Box>
        </Flex>
      </DialogUnwrapped>
    )}
  </Layout>
);

const RemoteInvite = () => {
  const { setRequestState } = useLoader();
  const navigate = useNavigate();

  const appointmentId = useAppointmentId();
  const {
    appointment,
    loading,
    hasCustomer,
    hasSupportingPartner,
    isRemoteSupport,
    personLabel,
  } = useAppointmentContext();
  const userId = localStorage.getItem(APPOINTMENT_USER_ID);

  useEffect(() => {
    setRequestState(loading ? RequestState.loading : RequestState.success);
  }, [setRequestState, loading]);

  if (loading) {
    return <Loading />;
  }

  if (!appointmentId && !loading) {
    navigate(paths.partner);
    return null;
  }

  if (!appointment) return null;

  const isController = userId === appointment.controller.id;

  const canStart = isRemoteSupport ? hasSupportingPartner : hasCustomer;

  const participantsSuffix = getCurrentParticipantsSuffix(
    appointment.participants
  );
  const errorMessage = isRemoteSupport
    ? 'You cannot start the appointment until the Supporting Partner joins'
    : `You cannot start the appointment until the customer joins${participantsSuffix}`;

  const title = isController
    ? isRemoteSupport
      ? 'Help your supporting Partner join'
      : 'Help others join your appointment'
    : 'Appointment will start soon';

  return (
    <RemoteInviteUI
      title={title}
      isController={isController}
      canStart={canStart}
      personLabel={personLabel}
      isRemoteSupport={isRemoteSupport}
      appointmentId={appointment.id}
      controllerName={appointment.controller.name}
      errorMessage={errorMessage}
    />
  );
};

export type { RemoteInviteUIProps };

export { RemoteInvite, RemoteInviteUI };
